<template>
    <app-layout title="Settings">
        <page-header>
            <template #title>Settings - Customer Flags</template>

            <template #actions>
                <jet-button type="button" class="h-7 leading-none" @click="showAddFlagModal = true" >
                    Add Flag
                </jet-button>
            </template>
        </page-header>

        <settings-navigation />

        <div class="px-8 mb-4 xl:flex">
            <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                <table class="table w-full">
                    <thead>
                        <tr>
                            <th class="text-left">Email</th>
                            <th class="text-left">Type</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="flag in flags" :key="flag.id">
                            <td>{{ flag.email }}</td>
                            <td>{{ flag.type === 'onetime' ? 'One-time' : 'Recurring' }}</td>
                            <td class="text-right">
                                <jet-secondary-button class="mx-4" @click="editFlag(flag)">
                                    Edit
                                </jet-secondary-button>

                                <jet-danger-button class="mx-4" @click="deleteFlag(flag)">
                                    Delete
                                </jet-danger-button>
                            </td>
                        </tr>
                        <tr v-if="!flags.length">
                            <td colspan="3" class="text-center py-8">No Flags Defined</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Add Flag -->
            <jet-dialog-modal :show="showAddFlagModal" @close="cancelAddFlag">
                <template #title>
                     <h3 class="text-2xl">Add Flag</h3>
                </template>

                <template #content>
                    <form @submit.prevent="addFlag" class="my-6">
                        <div class="flex gap-x-4 items-center mb-4">
                            <jet-label value="Email:" class="mb-0" style="width: 60px;" />
                            <jet-input v-model="form.email" type="email" class="w-1/2"/>
                            <jet-input-error :message="form.errors.email" />
                        </div>

                        <div class="flex gap-x-4 items-center">
                            <jet-label value="Type:" class="mb-0" style="width: 60px;" />
                            <multiselect
                                v-model="form.type"
                                :options="types"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="multi-lg h-7 text-base"
                                style="width: 50%; margin: unset;"
                                placeholder="Select number"
                            />
                            <jet-input-error :message="form.errors.type" />
                        </div>

                        <input type="submit" class="hidden" ref="submitAddFlag" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitAddFlag.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="cancelAddFlag">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Edit Flag -->
            <jet-dialog-modal :show="showEditFlagModal" @close="cancelEditFlag">
                <template #title>
                     <h3 class="text-2xl">Edit Flag</h3>
                </template>

                <template #content>
                    <form @submit.prevent="handleEditFlag" class="my-6">
                        <div class="flex gap-x-4 items-center mb-4">
                            <jet-label value="Email:" class="mb-0" style="width: 60px;" />
                            <jet-input v-model="form.email" type="email" class="w-1/2"/>
                            <jet-input-error :message="form.errors.email" />
                        </div>

                        <div class="flex gap-x-4 items-center">
                            <jet-label value="Type:" class="mb-0" style="width: 60px;" />
                            <multiselect
                                v-model="form.type"
                                :options="types"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="multi-lg h-7 text-base"
                                style="width: 50%; margin: unset;"
                                placeholder="Select number"
                            />
                            <jet-input-error :message="form.errors.type" />
                        </div>

                        <input type="submit" class="hidden" ref="submitEditFlag" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitEditFlag.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="cancelEditFlag">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Flag -->
            <jet-confirmation-modal :show="showDeleteFlagModal" @close="cancelDeleteFlag">
                <template #title>
                    Delete Flag
                </template>

                <template #content>
                    Are you sure you would like to delete <strong>{{ form.email }}</strong>?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteFlag">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="cancelDeleteFlag">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </div>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            id: '',
            email: '',
            type: '',
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        JetSecondaryButton,
        JetDialogModal,
        JetConfirmationModal,
        SettingsNavigation,
        Multiselect,
    },

    props: ['flags'],

    data() {
        return {
            showAddFlagModal: false,
            showEditFlagModal: false,
            showDeleteFlagModal: false,

            types: [
                { label: 'One-time', value: 'onetime' },
                { label: 'Recurring', value: 'recurring' },
            ],
        }
    },

    methods: {
        cancelAddFlag() {
            this.form.reset();
            this.form.clearErrors();
            this.showAddFlagModal = false;
        },

        addFlag() {
            this.form.post(this.route('settings.customer_flags.store'), {
                onSuccess: () => {
                    this.form.reset();
                    this.showAddFlagModal = false;
                }
            });
        },

        editFlag(flag) {
            this.form.id = flag.id;
            this.form.email = flag.email;
            this.form.type = flag.type;
            this.showEditFlagModal = true;
        },

        cancelEditFlag() {
            this.form.reset();
            this.form.clearErrors();
            this.showEditFlagModal = false;
        },

        handleEditFlag() {
            this.form.put(this.route('settings.customer_flags.update', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.form.clearErrors();
                    this.showEditFlagModal = false;
                }
            });
        },

        deleteFlag(flag) {
            this.form.id = flag.id;
            this.form.email = flag.email;
            this.showDeleteFlagModal = true;
        },

        cancelDeleteFlag() {
            this.form.reset();
            this.showDeleteFlagModal = false;
        },

        handleDeleteFlag() {
            this.form.delete(this.route('settings.customer_flags.destroy', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.showDeleteFlagModal = false;
                }
            });
        },
    },
}
</script>

<style scoped>
</style>
